import type { ReactElement } from 'react';
import { Divider, Grid, Stack } from '@mantine/core';
import { useWatch, type Control, type FieldValues, type Path, type UseFormRegister } from 'react-hook-form';
import {
  findUnassignedFieldNames,
  type FieldRegistry,
  type LayoutDocument,
  type LayoutFieldEntry,
} from './types';

/**
 * Generic layout-driven form renderer. The same component powers every
 * form's drawer (entry, contract, …) and the editor's live preview.
 *
 * - Each group becomes a Mantine `<Stack>` with an optional title `<Divider>`.
 * - Inside the group, fields render as `<Grid>` cells with the configured span.
 * - Conditional fields (registry's `visibleWhen`) are filtered against the
 *   live form values via `useWatch`.
 * - Fields with `hidden: true` in the layout are skipped entirely.
 * - Names not present in the registry are skipped silently (tolerates a
 *   layout that references a field that was renamed/removed).
 * - Names IN the registry but NOT in the layout fall into a sticky
 *   "Unassigned" group at the bottom — admins use it to place fresh
 *   registry entries that the active layout doesn't yet reference.
 */

interface RendererProps<TSchema extends FieldValues, TName extends string, TContext> {
  layout: LayoutDocument<TName>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<TSchema, any, any>;
  register: UseFormRegister<TSchema>;
  registry: FieldRegistry<TSchema, TName, TContext>;
  /** All names known to the registry; used to compute the Unassigned set. */
  allNames: readonly TName[];
  context: TContext;
  /** When true, all inputs render disabled (preview mode). */
  disabled?: boolean | undefined;
  /** When true, fields with `lockedOnEdit` render disabled. */
  isEdit?: boolean | undefined;
}

export function FormLayoutRenderer<
  TSchema extends FieldValues,
  TName extends string,
  TContext,
>(props: RendererProps<TSchema, TName, TContext>): ReactElement {
  const { layout, control, register, registry, allNames, context, disabled, isEdit } = props;
  const values = useWatch({ control });
  const unassigned = findUnassignedFieldNames(layout, allNames);

  return (
    <Stack gap="md">
      {layout.groups.map((g) => (
        <RenderedGroup
          key={g.id}
          title={g.title}
          fields={g.fields}
          values={values as Record<string, unknown>}
          control={control}
          register={register}
          registry={registry}
          context={context}
          disabled={disabled}
          isEdit={isEdit}
        />
      ))}

      {unassigned.length > 0 && (
        <RenderedGroup
          title="Unassigned (place these in the layout editor)"
          fields={unassigned.map((name) => ({ name, span: registry[name].defaultSpan }))}
          values={values as Record<string, unknown>}
          control={control}
          register={register}
          registry={registry}
          context={context}
          disabled={disabled}
          isEdit={isEdit}
        />
      )}
    </Stack>
  );
}

interface GroupProps<TSchema extends FieldValues, TName extends string, TContext> {
  title: string;
  fields: LayoutFieldEntry<TName>[];
  values: Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<TSchema, any, any>;
  register: UseFormRegister<TSchema>;
  registry: FieldRegistry<TSchema, TName, TContext>;
  context: TContext;
  disabled?: boolean | undefined;
  isEdit?: boolean | undefined;
}

function RenderedGroup<TSchema extends FieldValues, TName extends string, TContext>(
  props: GroupProps<TSchema, TName, TContext>,
): ReactElement | null {
  const { title, fields, values, control, register, registry, context, disabled, isEdit } = props;
  const visible = fields.filter((f) => {
    if (f.hidden) return false;
    if (!(f.name in registry)) return false;
    const desc = registry[f.name];
    if (desc.visibleWhen && !desc.visibleWhen(values)) return false;
    return true;
  });
  if (visible.length === 0) return null;

  return (
    <Stack gap="xs">
      <Divider label={title} labelPosition="left" />
      <Grid align="flex-start">
        {visible.map((f) => {
          const desc = registry[f.name];
          const isFieldDisabled = disabled === true || (isEdit === true && desc.lockedOnEdit === true);
          return (
            <Grid.Col key={f.name} span={{ base: 12, sm: f.span }}>
              {desc.render({
                name: f.name as unknown as Path<TSchema>,
                control,
                register,
                descriptor: desc,
                context,
                disabled: isFieldDisabled,
              })}
            </Grid.Col>
          );
        })}
      </Grid>
    </Stack>
  );
}
